import React from 'react';
import { Player, Team, SelectionData, SkillDistribution } from '../Model';
import { getSkillColor } from '../skillNames';

type AvailablePlayersProps = {
  selectionData: SelectionData | null;
  onPlayerSelect?: (player: Player) => void;
  selectedPlayer?: Player | null;
  maxPlayers?: number;
  onAddPlayerToTeam?: (playerId: number, teamId: number) => void;
  skillDistribution?: SkillDistribution;
  showArchived?: boolean;
  onShowArchivedChange?: (show: boolean) => void;
};

function AvailablePlayer({
  player,
  onPlayerSelect,
  isSelected,
  skillNames,
  teams,
  maxPlayers,
  onAddPlayerToTeam,
  skillDistribution,
  isArchived
}: {
  player: Player;
  onPlayerSelect?: (player: Player) => void;
  isSelected: boolean;
  skillNames: string[];
  teams?: Team[];
  maxPlayers?: number;
  onAddPlayerToTeam?: (playerId: number, teamId: number) => void;
  skillDistribution?: SkillDistribution;
  isArchived?: boolean;
}) {
  const colors = getSkillColor(player, skillDistribution);
  const skillsTooltip = `${colors.category} (${player.totalScore})\n${skillNames.map(s => `${s}: ${player.skills[s] ?? '-'}`).join(', ')}`;

  // Check if player is already in a team
  const isInTeam = teams?.some(team =>
    team.players?.some(p => p.id === player.id)
  ) ?? false;

  // Find teams that have space (less than maxPlayers)
  const teamsWithSpace = teams?.filter(team =>
    (team.players?.length ?? 0) < (maxPlayers ?? 9)
  ) ?? [];

  // Only show plus button if teams have been assigned and are actively showing players
  // Teams are considered "active" when they have players arrays (even if empty) rather than undefined
  const hasActiveTeams = teams && teams.length > 0 && teams.every(team => Array.isArray(team.players));
  const canAddToTeam = !isInTeam && teamsWithSpace.length > 0 && onAddPlayerToTeam && hasActiveTeams;

  const handleAddToTeam = (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    if (teamsWithSpace.length > 0 && onAddPlayerToTeam) {
      // Add to the first team with space
      onAddPlayerToTeam(player.id, teamsWithSpace[0].id);
    }
  };

  return (
    <div
      className={`available-player-row ${isSelected ? 'available-player-row-selected' : ''} ${isArchived ? 'available-player-row-archived' : ''}`}
      title={skillsTooltip}
      onClick={() => onPlayerSelect?.(player)}
      style={{ cursor: onPlayerSelect ? 'pointer' : 'default' }}
    >
      <div className="player-accent-bar" style={{ backgroundColor: colors.accent }} />
      <span className="available-player-name-text">{player.name}</span>
      <div className="available-player-right">
        <span className="available-player-score">{player.totalScore}</span>
        {canAddToTeam && (
          <button
            onClick={handleAddToTeam}
            className="available-player-add"
            title={`Add to ${teamsWithSpace[0]?.name || 'team'}`}
          >
            +
          </button>
        )}
      </div>
    </div>
  );
}

function AvailablePlayers({
  selectionData,
  onPlayerSelect,
  selectedPlayer,
  maxPlayers,
  onAddPlayerToTeam,
  skillDistribution,
  showArchived,
  onShowArchivedChange
}: AvailablePlayersProps) {
  const allPlayers = selectionData?.players || [];
  const skillNames = selectionData?.skillNames || [];
  const teams = selectionData?.teams || [];

  // Count archived players
  const archivedCount = allPlayers.filter(p => p.isArchived).length;

  // Filter players based on showArchived toggle
  const players = showArchived
    ? allPlayers
    : allPlayers.filter(p => !p.isArchived);

  return (
    <div className="available-players-panel">
      <div className="available-players-header">
        <h3 className="available-players-title">Players ({players.length})</h3>
        {archivedCount > 0 && onShowArchivedChange && (
          <label className="available-players-archived-toggle">
            <input
              type="checkbox"
              checked={showArchived || false}
              onChange={e => onShowArchivedChange(e.target.checked)}
            />
            <span>Show archived ({archivedCount})</span>
          </label>
        )}
      </div>
      <div>
        {players.length === 0 ? (
          <div className="drop-zone">
            {archivedCount > 0 ? 'All players are archived' : 'No players added yet'}
          </div>
        ) : (
          players.map((p) => (
            <AvailablePlayer
              key={p.id}
              player={p}
              onPlayerSelect={onPlayerSelect}
              isSelected={selectedPlayer?.id === p.id}
              skillNames={skillNames}
              teams={teams}
              maxPlayers={maxPlayers}
              onAddPlayerToTeam={onAddPlayerToTeam}
              skillDistribution={skillDistribution}
              isArchived={p.isArchived}
            />
          ))
        )}
      </div>
    </div>
  );
}

export default AvailablePlayers;
